import { Localized } from "@fluent/react";
import { Alert, Card, CardContent, CardHeader, Divider, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../../app/hooks";
import { showSuccess } from "../../../notifications/notificationsSlice";
import { CompanySetting, restoreCompanySettings, updateCompanySettings } from "../companySettingsAPI";
import { PasswordConfirmationDialog } from "../dialogs/PasswordConfirmationDialog";
import { RestoreSettingsPasswordConfirmationDialog } from "../dialogs/RestoreSettingsPasswordConfirmationDialog";
import { CardActionButtons } from "./CardActionButtons";

const filenameTemplates = {
    SUPPLIER_COMMENTED_PRINT_FILENAME_TEMPLATE: {
        titleId: "company-settings-urs-print-filename-title",
        title: "_Nazwa wydruku z komentarzami dostawcy",
        missingId: "company-settings-urs-print-filename-missing",
        missing: "_Nie znaleziono ustawienia szablonu nazwy wydruku z komentarzami dostawcy.",
    },
    SUPPLIER_COMMENTED_EXCEL_FILENAME_TEMPLATE: {
        titleId: "company-settings-urs-excel-filename-title",
        title: "_Nazwa pliku Excel z komentarzami dostawcy",
        missingId: "company-settings-urs-excel-filename-missing",
        missing: "_Nie znaleziono ustawienia szablonu nazwy pliku Excel z komentarzami dostawcy.",
    },
    DOCUMENT_PRINT_FILENAME_TEMPLATE: {
        titleId: "company-settings-document-print-filename-title",
        title: "_Nazwa wydruku dokumentu",
        missingId: "company-settings-document-print-filename-missing",
        missing: "_Nie znaleziono ustawienia szablonu nazwy wydruku dokumentu.",
    },
    DOCUMENT_EXCEL_FILENAME_TEMPLATE: {
        titleId: "company-settings-document-excel-filename-title",
        title: "_Nazwa pliku Excel dokumentu",
        missingId: "company-settings-document-excel-filename-missing",
        missing: "_Nie znaleziono ustawienia szablonu nazwy pliku Excel dokumentu.",
    },
} as const;

interface Props {
    code: keyof typeof filenameTemplates;
    secondaryCode?: keyof typeof filenameTemplates;
    groupTitle?: { id: string; fallback: string };
    companySettings: CompanySetting[];
    onUpdate: (settings: CompanySetting[]) => void;
}

export const CompanyFilenameTemplateSettingCard = ({ code, secondaryCode, groupTitle, companySettings, onUpdate }: Props) => {
    const dispatch = useAppDispatch();
    const template = filenameTemplates[code];
    const codes = useMemo(() => secondaryCode ? [code, secondaryCode] : [code], [code, secondaryCode]);
    const settings = codes.map(itemCode => companySettings.find(item => item.code === itemCode));
    const [values, setValues] = useState<Record<string, string>>(() =>
        Object.fromEntries(codes.map((itemCode, index) => [itemCode, settings[index]?.value ?? ""]))
    );
    const [isEditing, setIsEditing] = useState(false);
    const [dialog, setDialog] = useState<"save" | "restore" | null>(null);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    useEffect(() => {
        setValues(Object.fromEntries(codes.map(itemCode => [
            itemCode, companySettings.find(item => item.code === itemCode)?.value ?? "",
        ])));
    }, [companySettings, codes]);

    const missingCode = codes.find(itemCode => !companySettings.some(item => item.code === itemCode));
    if (missingCode) {
        const missingTemplate = filenameTemplates[missingCode];
        return (
            <Alert severity="error">
                <Localized id={missingTemplate.missingId}>{missingTemplate.missing}</Localized>
            </Alert>
        );
    }

    const editableSettings = settings.filter((setting): setting is CompanySetting => !!setting && !setting.isFixed);

    const closeDialog = () => {
        setDialog(null);
        setServerError("");
    };

    const confirm = async (password: string) => {
        if (!password || !dialog) return;
        setSaving(true);
        setServerError("");

        try {
            const result = dialog === "save"
                ? await dispatch(updateCompanySettings({
                    password,
                    settings: editableSettings.map(setting => ({ ...setting, value: values[setting.code] })),
                }))
                : await dispatch(restoreCompanySettings({
                    password,
                    settings: editableSettings.map(setting => setting.code),
                }));

            if (updateCompanySettings.fulfilled.match(result) || restoreCompanySettings.fulfilled.match(result)) {
                onUpdate(result.payload);
                closeDialog();
                setIsEditing(false);
                dispatch(showSuccess("saved"));
            } else {
                setServerError(result.payload?.kind === "http"
                    ? result.payload.problem?.title ?? "error"
                    : "error");
            }
        } catch (error) {
            console.error(error);
            setServerError("error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card sx={{ maxWidth: 500, borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
                title={<Localized id={groupTitle?.id ?? template.titleId}>{groupTitle?.fallback ?? template.title}</Localized>}
                sx={{
                    textAlign: "center",
                    bgcolor: "primary.main",
                    color: "white",
                    py: 1,
                    "& .MuiCardHeader-title": { fontSize: "1rem" },
                }}
            />
            <CardContent>
                {codes.map(itemCode => {
                    const item = companySettings.find(setting => setting.code === itemCode)!;
                    const itemTemplate = filenameTemplates[itemCode];
                    return (
                        <TextField
                            key={itemCode}
                            fullWidth
                            size="small"
                            margin={codes.length > 1 ? "normal" : "none"}
                            label={codes.length > 1
                                ? <Localized id={itemTemplate.titleId}>{itemTemplate.title}</Localized>
                                : <Localized id="company-settings-urs-print-filename-template">_Szablon nazwy pliku</Localized>}
                            value={values[itemCode] ?? ""}
                            onChange={event => setValues(previous => ({ ...previous, [itemCode]: event.target.value }))}
                            disabled={!isEditing || item.isFixed}
                        />
                    );
                })}
                {editableSettings.length > 0 && (
                    <>
                        <Divider sx={{ mt: 3 }} />
                        <CardActionButtons
                            isDefault={editableSettings.every(setting => setting.isDefault)}
                            isEditing={isEditing}
                            onRestore={() => setDialog("restore")}
                            onSave={() => setDialog("save")}
                            onCancel={() => {
                                setValues(Object.fromEntries(codes.map(itemCode => [
                                    itemCode, companySettings.find(item => item.code === itemCode)?.value ?? "",
                                ])));
                                setIsEditing(false);
                            }}
                            onEdit={() => setIsEditing(true)}
                        />
                    </>
                )}
            </CardContent>
            {dialog === "save" && (
                <PasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={closeDialog}
                    onConfirm={confirm}
                />
            )}
            {dialog === "restore" && (
                <RestoreSettingsPasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={closeDialog}
                    onConfirm={confirm}
                />
            )}
        </Card>
    );
};
